import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  Button,
  Alert,
  Row,
  Col,
  Tag,
  Typography,
} from "antd";
import {
  InfoCircleOutlined,
  SaveOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { createRange } from "../Services/bibService";
import { rangeKey } from "../hooks/useBib";
import type { TicketOption } from "./TicketFilter";

const { Text } = Typography;

const SORT_OPTIONS = [
  { label: "Registration Date (earliest first)", value: "registration_date" },
  { label: "Female First", value: "female_first" },
  { label: "First Name (A–Z)", value: "first_name" },
  { label: "Last Name (A–Z)", value: "last_name" },
];

interface RangeInfo {
  start: number;
  end: number;
  sort_by: string;
  prefix?: string;
  ticket_id?: string;
  ticket_name?: string;
  id?: string;
}

interface BibRangeModalProps {
  open: boolean;
  onClose: () => void;
  eventId?: string;
  distances: string[];
  tickets?: TicketOption[];
  defaultDistance?: string;
  defaultTicketId?: string;
  savedRanges?: Record<string, RangeInfo>;
  onSuccess: (key: string, rangeInfo: RangeInfo) => void;
}

const BibRangeModal: React.FC<BibRangeModalProps> = ({
  open,
  onClose,
  eventId,
  distances,
  tickets = [],
  defaultDistance,
  defaultTicketId,
  savedRanges = {},
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [selectedFormDistance, setSelectedFormDistance] = useState<
    string | undefined
  >(undefined);
  const [selectedFormTicket, setSelectedFormTicket] = useState<
    string | undefined
  >(undefined);

  const startNum = Form.useWatch("start_number", form);
  const endNum = Form.useWatch("end_number", form);
  const autoCapacity =
    startNum > 0 && endNum > startNum ? endNum - startNum + 1 : undefined;

  const ticketsForDistance = tickets.filter(
    (t) => t.distance === (selectedFormDistance || defaultDistance),
  );

  // Existing ranges across the entire event (to show what's already taken)
  const allExistingRanges = useMemo(() => {
    return Object.entries(savedRanges)
      .filter(([k]) => k.includes("___")) // only ticket-level entries
      .map(([k, v]) => ({
        key: k,
        ...v,
        distance: k.split("___")[0],
        isCurrentTicket: v.ticket_id === selectedFormTicket,
      }));
  }, [savedRanges, selectedFormTicket]);

  // Whether the selected ticket already has a range
  const currentTicketRange = useMemo(() => {
    if (!selectedFormTicket) return null;
    const dist = selectedFormDistance || defaultDistance;
    const key = rangeKey(dist ?? "", selectedFormTicket);
    return savedRanges[key] ?? null;
  }, [savedRanges, selectedFormDistance, defaultDistance, selectedFormTicket]);

  useEffect(() => {
    if (open) {
      setSelectedFormDistance(defaultDistance);
      setSelectedFormTicket(defaultTicketId);
      form.setFieldsValue({
        distance: defaultDistance ?? undefined,
        ticket_id: defaultTicketId ?? undefined,
        start_number: currentTicketRange?.start,
        end_number: currentTicketRange?.end,
        sort_by: currentTicketRange?.sort_by ?? "registration_date",
        prefix: currentTicketRange?.prefix ?? "",
      });
    }
  }, [open, defaultDistance, defaultTicketId, form]);

  const validateNoOverlap = (
    newStart: number,
    newEnd: number,
    ticketId: string,
    distance: string,
  ): string | null => {
    const thisKey = rangeKey(distance, ticketId);

    for (const [key, range] of Object.entries(savedRanges)) {
      if (key === thisKey) continue; // same ticket — allow overwrite
      // Skip plain distance keys (non-ticket entries)
      if (!key.includes("___")) continue;

      if (newStart <= range.end && newEnd >= range.start) {
        const distancePart = key.split("___")[0];
        const name = range.ticket_name || key;
        const label = distancePart !== distance
          ? `"${name}" on ${distancePart}`
          : `"${name}"`;
        return `Range ${newStart}–${newEnd} overlaps with ${label} (BIB ${range.start}–${range.end}). BIB ranges must be unique across the entire event.`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!eventId) return;

      const { distance, ticket_id, start_number, end_number } = values;
      const selectedTicket = tickets.find((t) => t.id === ticket_id);

      // Cross-ticket overlap validation
      const overlapError = validateNoOverlap(
        start_number,
        end_number,
        ticket_id,
        distance,
      );
      if (overlapError) {
        form.setFields([{ name: "end_number", errors: [overlapError] }]);
        return;
      }

      setSaving(true);

      await createRange({
        event_id: eventId,
        distance,
        ticket_id,
        start_number,
        end_number,
        sort_by: values.sort_by,
        prefix: values.prefix || "",
      });

      const key = rangeKey(distance, ticket_id);
      onSuccess(key, {
        start: start_number,
        end: end_number,
        sort_by: values.sort_by,
        prefix: values.prefix,
        ticket_id,
        ticket_name: selectedTicket?.name,
        id: currentTicketRange?.id,
      });
      form.resetFields(["start_number", "end_number", "prefix"]);
      onClose();
    } catch (err: any) {
      if (err?.errorFields) return;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedFormDistance(undefined);
    setSelectedFormTicket(undefined);
    onClose();
  };

  return (
    <Modal
      title="Define BIB Range & Sorting Policy"
      open={open}
      onCancel={handleCancel}
      destroyOnClose
      width={560}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          Save Range
        </Button>,
      ]}
    >
      {/* Existing ranges hint — show all ranges across the event */}
      {allExistingRanges.filter((r) => !r.isCurrentTicket).length > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
          message="Existing BIB ranges in this event"
          description={
            <div
              style={{
                marginTop: 4,
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
              }}
            >
              {allExistingRanges
                .filter((r) => !r.isCurrentTicket)
                .map((r) => (
                  <Tag key={r.key} color="blue" style={{ fontSize: 11 }}>
                    {r.distance} / {r.ticket_name || r.key}: {r.start}–{r.end}
                  </Tag>
                ))}
            </div>
          }
        />
      )}

      {/* Overwrite warning */}
      {currentTicketRange && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <span>
              This ticket already has a BIB range{" "}
              <Text strong>
                {currentTicketRange.start}–{currentTicketRange.end}
              </Text>
              . Saving will overwrite it.
            </span>
          }
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{ sort_by: "registration_date" }}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="distance"
              label="Distance"
              rules={[{ required: true, message: "Select a distance" }]}
            >
              <Select
                placeholder="Select distance"
                options={distances.map((d) => ({ label: d, value: d }))}
                onChange={(v) => {
                  setSelectedFormDistance(v);
                  setSelectedFormTicket(undefined);
                  form.setFieldValue("ticket_id", undefined);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="ticket_id"
              label="Ticket"
              rules={[{ required: true, message: "Select a ticket" }]}
            >
              <Select
                placeholder="Select ticket"
                disabled={ticketsForDistance.length === 0}
                options={ticketsForDistance.map((t) => ({
                  label: t.name,
                  value: t.id,
                }))}
                onChange={(v) => setSelectedFormTicket(v)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="start_number"
              label="Start BIB"
              rules={[
                { required: true, message: "Required" },
                { type: "integer", min: 1, message: "Must be ≥ 1" },
              ]}
            >
              <InputNumber
                min={1}
                placeholder="e.g. 1001"
                className="w-full"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="end_number"
              label="End BIB"
              dependencies={["start_number"]}
              rules={[
                { required: true, message: "Required" },
                ({ getFieldValue }) => ({
                  validator(_, val) {
                    if (!val) return Promise.resolve();
                    const start = getFieldValue("start_number");
                    if (val <= start)
                      return Promise.reject(
                        new Error("Must be greater than Start BIB"),
                      );

                    // Real-time cross-ticket overlap
                    const dist = getFieldValue("distance") || defaultDistance;
                    const ticketId = getFieldValue("ticket_id");
                    if (dist && ticketId) {
                      const err = validateNoOverlap(start, val, ticketId, dist);
                      if (err) return Promise.reject(new Error(err));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                min={1}
                placeholder="e.g. 1999"
                className="w-full"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Auto-calculated capacity */}
        <Form.Item label="Capacity (auto-calculated)">
          <InputNumber
            value={autoCapacity}
            disabled
            className="w-full"
            style={{ width: "100%" }}
            placeholder="Set BIB start & end above"
            formatter={(v) => (v ? `${v} slots` : "")}
          />
        </Form.Item>

        <Form.Item name="sort_by" label="Sorting Rule">
          <Select options={SORT_OPTIONS} />
        </Form.Item>

        <Form.Item name="prefix" label="BIB Prefix (optional)">
          <Input placeholder="e.g. RUN" maxLength={10} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BibRangeModal;
